// ai/pipelines/research-team.ts
//
// Turns a StoryBrain brand into a REAL roster, via the web-searching
// `research-team` prompt, and formats it as an authoritative block for the
// rewrite pass.
//
// The problem it solves: nothing in the prompt ever contained the user's actual
// colleagues. StoryBrain carries brand name, domain, tone and description, plus
// knowledge links (title + 240 chars) and file NAMES — contents are not inlined.
// So every team slide named plausible strangers, and no amount of prompt
// pressure could change that, because the information simply was not there.
//
// Fails soft in every direction. No brand name, a research error, an
// unconfirmed company, an empty roster — all return null, and the caller falls
// back to the fictional-placeholder path it had before. A team slide with
// invented names is a poor result; a team slide with the WRONG real names is a
// false claim on someone's deck, so "no roster" must always beat "a guess".

import type { StoryBrainContext } from "../context/story-brain";

export type ResearchedPerson = {
  name: string;
  role: string;
  oneLiner: string;
  sourceUrl: string;
  // Direct headshot url off the company's team page, when the page showed one.
  // The primary source of a real photo: these people are not on Wikipedia, and
  // its name lookup used to fuzzy-resolve to an arbitrary article.
  photoUrl?: string;
};

export type TeamResearchResult = {
  people: ResearchedPerson[];
  companyConfirmed: boolean;
};

export type RunPrompt = (args: {
  promptId: string;
  variables: Record<string, unknown>;
}) => Promise<{ result: unknown }>;

/**
 * The company this deck belongs to, or null when StoryBrain cannot name it.
 * A domain alone is enough — "storydoc.com" identifies the company even if the
 * brand name field is blank.
 */
export const companyFromBrain = (
  brain: StoryBrainContext | null | undefined,
): { name: string; domain: string; context: string } | null => {
  const brand = brain?.brand;
  const name = brand?.name?.trim() || "";
  const domain = brand?.domain?.trim() || "";
  if (!name && !domain) return null;
  return {
    name: name || domain,
    domain,
    context: brand?.description?.trim().slice(0, 800) || "",
  };
};

const nameWords = (name: string): string[] =>
  String(name ?? "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);

/** A name with nothing after the first word: "Yaacov", not "Yaacov Goldberg". */
export const isFirstNameOnly = (name: string): boolean =>
  nameWords(name).length === 1;

/**
 * The people the research named without a surname.
 *
 * A roster where some people have a surname and others do not is almost never
 * what a page really says -- it is what happens when the model reads the line
 * that introduces the founders ("Founded by Itai, Daniel and Yaacov") and
 * stops, without reading down to the team section that lists them in full.
 * Observed on Storydoc's own page, which carries both: the slide came back
 * "Itai Amoza", "Yaacov", "Daniel".
 *
 * Prompt rule 5b already tells the model to look further down and it does not
 * reliably do it, which is why this is a check on the ANSWER rather than more
 * words in the question.
 */
export const namesNeedingSurnames = (people: ResearchedPerson[]): string[] => {
  const incomplete = people.filter((p) => isFirstNameOnly(p.name));
  // All of them first-name-only can be the truth -- a page that never gives a
  // surname to anybody. The mixed roster is the tell, because the same page
  // clearly does publish surnames.
  if (incomplete.length === 0 || incomplete.length === people.length) return [];
  return incomplete.map((p) => p.name.trim());
};

/** The instruction for a second attempt, assembled here because the server's
 * interpolator has no conditionals on purpose. Empty when nothing is missing. */
export const surnameRetryNote = (missing: string[]): string =>
  missing.length === 0
    ? ""
    : [
        "\n\nSECOND ATTEMPT — SURNAMES ONLY.",
        `A previous answer returned these people with a first name and no surname: ${missing.join(", ")}.`,
        'That is almost never how the page really reads. A company that introduces its founders as "Founded by Itai, Daniel and Yaacov" nearly always lists them in full lower down — in the team, leadership or about section of the SAME page, each name above a job title. Open that page again and read that section before answering.',
        "Return the same roster, in the same order, with those names completed. Change nothing else. If a surname genuinely is not published anywhere, return that person with the first name exactly as it is — never invent, initial or guess a surname, and never drop the person to avoid the problem.",
      ].join("\n");

/**
 * Take the completed names from a second attempt, and nothing else.
 *
 * Matched on the first name, which is the only part the two answers are
 * guaranteed to share. A name is only ever replaced by a LONGER one for the
 * same person, so a retry that comes back worse -- or that quietly swaps
 * somebody out -- cannot damage a roster that was already right.
 *
 * Names only. Roles, one-liners and photos stay exactly as the first answer
 * had them: this ran to fix surnames, and a second opinion on everything else
 * is a second chance to be wrong about it.
 */
export const mergeSurnames = (
  original: ResearchedPerson[],
  retried: ResearchedPerson[],
): ResearchedPerson[] =>
  original.map((person) => {
    if (!isFirstNameOnly(person.name)) return person;
    const first = nameWords(person.name)[0]?.toLowerCase();
    const better = retried.find(
      (candidate) =>
        nameWords(candidate.name)[0]?.toLowerCase() === first &&
        nameWords(candidate.name).length > 1,
    );
    return better ? { ...person, name: better.name.trim() } : person;
  });

/**
 * Research the deck's real team. Returns null whenever the answer would be a
 * guess — see the soft-failure note above.
 */
export const researchTeam = async (
  runPrompt: RunPrompt,
  brain: StoryBrainContext | null | undefined,
  wanted: number,
  // A company the REQUEST named, which wins over the deck's brand. "Add a
  // slide about storydoc team" on a Shopify-branded deck is about Storydoc;
  // researching the brand there would put real people from the wrong company
  // on the slide, which is the failure this whole pipeline exists to avoid.
  // Only the name is known from a sentence, so the brand's domain and
  // description come along only when they describe the same company.
  namedCompany?: string | null,
  // Which people the slide is about. "founders" when the request named them
  // specifically -- without it the prompt answers every request with the
  // company's CURRENT leadership page, which for "Microsoft founders" returned
  // Nadella, Hood, Smith and Suleyman. None of them founded Microsoft, and the
  // caller then discarded the contradictory roster and invented a founder list
  // of its own, Gabe Newell included.
  focus: "" | "founders" = "",
): Promise<ResearchedPerson[] | null> => {
  const fromBrain = companyFromBrain(brain);
  const named = namedCompany?.trim() || "";
  const sameAsBrand =
    !!named &&
    !!fromBrain &&
    (fromBrain.name.toLowerCase().includes(named.toLowerCase()) ||
      fromBrain.domain.toLowerCase().includes(named.toLowerCase()));
  const company = named
    ? sameAsBrand
      ? fromBrain
      : { name: named, domain: "", context: "" }
    : fromBrain;
  if (!company) return null;

  const ask = async (retryNote: string): Promise<ResearchedPerson[] | null> => {
    let result: unknown;
    try {
      ({ result } = await runPrompt({
        promptId: "research-team",
        variables: {
          companyName: company.name,
          companyDomain: company.domain,
          companyContext: company.context,
          wanted: Math.max(1, Math.min(12, wanted || 3)),
          focus,
          retryNote,
        },
      }));
    } catch {
      return null;
    }

    const research = result as TeamResearchResult | null;
    // companyConfirmed false means the model found people but is not sure they
    // are from THIS company — exactly the case that puts a stranger on the deck.
    if (!research?.companyConfirmed) return null;
    const found = (research.people ?? []).filter(
      (p) => p && p.name?.trim() && p.sourceUrl?.trim(),
    );
    return found.length > 0 ? found : null;
  };

  const people = await ask("");
  if (!people) return null;

  // ONE MORE ASK, FOR THE SURNAMES ONLY.
  //
  // See namesNeedingSurnames: a roster carrying some surnames and not others
  // is the signature of an answer that read the founders' introduction and not
  // the team section below it. Costs a second search only in that case, and
  // only the completed names are taken from it.
  const missing = namesNeedingSurnames(people);
  if (missing.length === 0) return people;
  const retried = await ask(surnameRetryNote(missing));
  // A failed retry is not a failed roster: the first answer is still real
  // people with real roles, just short a surname or two.
  return retried ? mergeSurnames(people, retried) : people;
};

/**
 * Render the roster for the rewrite prompt. Named REAL TEAM so the prompt's
 * existing rule — "if the knowledge material NAMES actual people, use those
 * names and their stated roles exactly" — has something unambiguous to bind to.
 */
/**
 * Just the founders, when that is who was asked for.
 *
 * Falls back to the whole roster when the research found nobody whose title
 * says founder: a team slide is a weak answer to a founders request, but an
 * EMPTY one is not an answer at all, and the names are real either way.
 */
export const foundersOnly = (
  people: ResearchedPerson[],
): ResearchedPerson[] => {
  // ROLE OR ONE-LINER, because the research does not consistently put it in
  // either. One run answered with role "Co-Founder & CEO"; the next, for the
  // same company off the same page, answered role "CEO" with "Co-founder and
  // Chief Executive Officer of Storydoc." as the one-liner. Testing only the
  // role matched nobody on the second shape, and the fall-through below then
  // handed back the whole roster -- so the Head of Sales stayed on a founders
  // slide and the filter looked like it had simply not run.
  const founders = people.filter((p) =>
    /\bfounder\b/i.test(`${p.role ?? ""} ${p.oneLiner ?? ""}`),
  );
  return founders.length > 0 ? founders : people;
};

export const formatRosterForPrompt = (
  people: ResearchedPerson[],
  // Whether the request named the founders specifically, which changes how
  // many items the slide should have -- not just which ones.
  foundersRequested = false,
): string =>
  [
    "REAL TEAM (verified from the company's own sources — use these exact names and titles, in this order; do NOT invent anyone else):",
    ...people.map(
      (p, i) =>
        `  ${i + 1}. ${p.name} — ${p.role}${p.oneLiner ? ` — ${p.oneLiner}` : ""}`,
    ),
    // The layout resizes to the item count, so "write exactly N" is what makes
    // a four-card layout become a three-card one. Without it the model filled
    // the spare slot with the next person on the roster -- putting the Head of
    // Sales on a slide titled "Meet the Founders".
    foundersRequested
      ? `The request asked for the FOUNDERS. There are exactly ${people.length}. Write exactly ${people.length} item${people.length === 1 ? "" : "s"} — the layout resizes to fit, so do NOT add anyone else to fill a spare slot.`
      : "",
    "Say only what is listed above about each person. Do not add a previous employer, a tenure or a career history that is not written here — those read as researched and are not.",
    // NAMES IN FULL. The blueprint shortened them to "Yaacov" and "Daniel"
    // beside a heading that read "Meet the Storydoc Founders", which is how a
    // team slide starts looking like a list of first names on a rota.
    "Write each person's FULL name exactly as written above — both parts, never just the first name.",
    // The same sentence with the names swapped is not three facts, it is one
    // fact and two copies. Seen live: "Itai is one of the three co-founders of
    // Storydoc." / "Yaacov is one of the three co-founders of Storydoc." /
    // "Daniel is one of the three co-founders of Storydoc." -- a slide that
    // took a web search to produce and says nothing a reader did not already
    // have from the title.
    "Each person's line must say something DIFFERENT and specific about THAT person — what they lead, build or own. Do not restate their title, do not write a variation of the same sentence for each of them, and never say only that they are a founder: the heading already says so.",
  ]
    .filter(Boolean)
    .join("\n");

/**
 * Restore any heading the blueprint shortened to a first name.
 *
 * The roster block asks for full names in as many words, and the model still
 * answered "Yaacov" and "Daniel" beside a heading reading "Meet the Storydoc
 * Founders" -- a slide of first names, as if off a rota. A prompt is a
 * request; this is the guarantee.
 *
 * Deliberately narrow. It only expands a heading that IS a leading part of
 * exactly one person's full name, so "Daniel" becomes "Daniel Macbubi" while
 * anything ambiguous, already complete, or about somebody else is left alone.
 */
export const expandToFullNames = <T extends { heading?: string }>(
  items: T[],
  people: ResearchedPerson[],
): T[] => {
  const names = people.map((p) => (p.name ?? "").trim()).filter(Boolean);
  if (names.length === 0) return items;
  return items.map((item) => {
    const heading = (item?.heading ?? "").trim();
    if (!heading) return item;
    const lower = heading.toLowerCase();
    const matches = names.filter((full) => {
      const f = full.toLowerCase();
      // A LEADING part, so "Daniel" matches "Daniel Macbubi" but "Macbubi"
      // does not -- a surname alone is a deliberate style, not a truncation.
      return f !== lower && f.startsWith(`${lower} `);
    });
    // Exactly one, or expanding "Dan" on a team with two Dans picks a person
    // at random and puts the wrong surname on their card.
    return matches.length === 1 ? { ...item, heading: matches[0] } : item;
  });
};
